import { dateOf, hoursFromNow, timeOf, today, zonedInstant } from '../format/dates'
import { toMinutes, toTime } from './schedule'
import { OWNER_STAFF_ID, salonDayFor, upcomingSalonDays } from './salonDays'
import type { OpeningDay, SalonDayException } from '../types/site'
import type { AgendaAppointment, AgendaBlock, AvailabilityDay } from '../types/agenda'

/**
 * De regels van de agenda. Puur: dezelfde functies rekenen in het beheer (wat Sarah ziet),
 * in de demo-backend (wat de webshop als vrij toont) en straks als voorbeeld voor de server,
 * die de uiteindelijke waarheid bewaakt (dubbel boeken blokkeren met een lock, LARAVEL.md §11).
 */

/** Opruimen en klaarzetten na elke afspraak. Geldt niet vóór een blokkade (pauze na een klant is prima). */
export const BUFFER_MINUTES = 15
/** Starttijden voor klanten: op het halfuur. Sarah zelf kan per kwartier inplannen. */
export const SHOP_STEP_MINUTES = 30
export const ADMIN_STEP_MINUTES = 15
/** De herinnering met agenda-uitnodiging gaat zo lang vooraf. */
export const REMINDER_HOURS = 24
/** De eigenaar; ook de uitvoerder als een salondag niemand anders noemt (core/rules/salonDays.ts). */
export const DEFAULT_STAFF_ID = OWNER_STAFF_ID
/** Als een behandeling niet (meer) in de catalogus staat. */
export const FALLBACK_DURATION = 60

export type TimeSpan = { date: string; time: string; durationMinutes: number; staffId: string }

export type Conflict = { kind: 'appointment'; item: AgendaAppointment } | { kind: 'block'; item: AgendaBlock }

/** Telt nog mee in de planning: niet afgerond, niet geannuleerd, niet weggebleven. */
export const isActive = (a: Pick<AgendaAppointment, 'outcome'>) => !a.outcome

export const startMinutes = (s: Pick<TimeSpan, 'time'>) => toMinutes(s.time)
export const endMinutes = (s: Pick<TimeSpan, 'time' | 'durationMinutes'>) => toMinutes(s.time) + s.durationMinutes
export const endTime = (s: Pick<TimeSpan, 'time' | 'durationMinutes'>) => toTime(endMinutes(s))

/** Overlappen twee tijdvakken van dezelfde behandelaar op dezelfde dag? `buffer` komt achter allebei. */
export function overlaps(a: TimeSpan, b: TimeSpan, buffer = 0): boolean {
  if (a.date !== b.date || a.staffId !== b.staffId) return false
  return startMinutes(a) < endMinutes(b) + buffer && startMinutes(b) < endMinutes(a) + buffer
}

/** Waar een nieuwe of verzette afspraak mee botst. `ignoreId`: de afspraak die je verzet. */
export function conflictsFor(
  candidate: TimeSpan,
  appointments: AgendaAppointment[],
  blocks: AgendaBlock[],
  opts: { ignoreId?: string; buffer?: number } = {},
): Conflict[] {
  const buffer = opts.buffer ?? BUFFER_MINUTES
  return [
    ...appointments
      .filter((a) => isActive(a) && a.id !== opts.ignoreId && overlaps(candidate, a, buffer))
      .map((item): Conflict => ({ kind: 'appointment', item })),
    ...blocks.filter((b) => b.id !== opts.ignoreId && overlaps(candidate, b)).map((item): Conflict => ({ kind: 'block', item })),
  ]
}

/** Valt dit tijdvak (deels) buiten de salondag (uitzonderingen tellen mee), of is de salon die dag dicht? */
export function outsideHours(
  hours: OpeningDay[],
  span: Pick<TimeSpan, 'date' | 'time' | 'durationMinutes'>,
  exceptions?: readonly SalonDayException[],
): boolean {
  const w = salonDayFor(hours, exceptions, span.date)
  if (!w) return true
  return startMinutes(span) < toMinutes(w.open) || endMinutes(span) > toMinutes(w.close)
}

type FreeOptions = {
  /** Voor wie. Leeg = wie er die dag volgens de salondagen werkt. */
  staffId?: string
  step?: number
  ignoreId?: string
  now?: Date
  /** Uitzonderingen op de vaste week (vakantie, extra dag, ruil). */
  exceptions?: readonly SalonDayException[]
  /** Mag deze persoon de behandeling doen? Nee = die dag geen tijden (`canPerform`). */
  canDo?: (staffId: string) => boolean
}

/**
 * Vrije starttijden op één dag voor een behandeling van `durationMinutes`: binnen de
 * salondag, klaar vóór sluiten, zonder botsing en niet in het verleden.
 */
export function freeTimes(
  hours: OpeningDay[],
  date: string,
  durationMinutes: number,
  appointments: AgendaAppointment[],
  blocks: AgendaBlock[],
  opts: FreeOptions = {},
): string[] {
  const w = salonDayFor(hours, opts.exceptions, date)
  if (!w) return []
  const step = opts.step ?? SHOP_STEP_MINUTES
  const staffId = opts.staffId ?? w.staffId
  if (opts.canDo && !opts.canDo(staffId)) return []
  const now = opts.now ?? new Date()
  const times: string[] = []
  for (let t = toMinutes(w.open); t + durationMinutes <= toMinutes(w.close); t += step) {
    const time = toTime(t)
    if (zonedInstant(date, time).getTime() <= now.getTime()) continue
    const candidate = { date, time, durationMinutes, staffId }
    if (conflictsFor(candidate, appointments, blocks, { ignoreId: opts.ignoreId }).length === 0) times.push(time)
  }
  return times
}

/**
 * Vrije tijden voor de komende salondagen (vanaf morgen), voor de webshop. Alleen dagen waarop
 * iemand werkt die de behandeling doet (`canDo`); met `staffId` alleen de dagen van die persoon
 * ("Alleen bij Sarah"). Elke dag zegt wie het doet: de klant kiest de dag, de dag bepaalt de persoon.
 */
export function availability(
  hours: OpeningDay[],
  days: number,
  durationMinutes: number,
  appointments: AgendaAppointment[],
  blocks: AgendaBlock[],
  opts: FreeOptions & { from?: Date } = {},
): AvailabilityDay[] {
  const accept = (staffId: string) => (!opts.staffId || staffId === opts.staffId) && (!opts.canDo || opts.canDo(staffId))
  return upcomingSalonDays(hours, opts.exceptions, days, { from: opts.from, accept: (d) => accept(d.staffId) }).map((d) => ({
    date: d.date,
    staffId: d.staffId,
    times: freeTimes(hours, d.date, durationMinutes, appointments, blocks, { ...opts, staffId: d.staffId }),
  }))
}

/**
 * Naast elkaar zetten wat elkaar overlapt (bijv. een bewuste dubbele boeking): elk item krijgt
 * een baan en het aantal banen in zijn groep, zodat niets onzichtbaar onder iets anders valt.
 */
export function layoutLanes(items: { id: string; time: string; durationMinutes: number }[]): Map<string, { lane: number; lanes: number }> {
  const sorted = [...items].sort((a, b) => startMinutes(a) - startMinutes(b) || b.durationMinutes - a.durationMinutes)
  const result = new Map<string, { lane: number; lanes: number }>()
  let group: { id: string; lane: number }[] = []
  let laneEnds: number[] = []
  let groupEnd = -1
  const close = () => {
    group.forEach((g) => result.set(g.id, { lane: g.lane, lanes: laneEnds.length }))
    group = []
    laneEnds = []
  }
  sorted.forEach((item) => {
    const start = startMinutes(item)
    if (start >= groupEnd) close()
    let lane = laneEnds.findIndex((end) => end <= start)
    if (lane === -1) lane = laneEnds.length
    laneEnds[lane] = endMinutes(item)
    groupEnd = Math.max(groupEnd, endMinutes(item))
    group.push({ id: item.id, lane })
  })
  close()
  return result
}

/** Het exacte begin van een afspraak (salontijd). */
export const startsAt = (a: Pick<TimeSpan, 'date' | 'time'>) => zonedInstant(a.date, a.time)
export const hasStarted = (a: Pick<TimeSpan, 'date' | 'time'>, now = new Date()) => startsAt(a).getTime() <= now.getTime()

export type ReminderState = 'verstuurd' | 'gepland' | 'te-kort' | 'geen'

/**
 * Hoe staat de herinnering ervoor? Alleen bevestigde afspraken krijgen er een. Geboekt binnen
 * 24 uur vooraf: geen herinnering, de bevestiging is dan vers genoeg.
 */
export function reminderState(a: AgendaAppointment, now = new Date()): ReminderState {
  if (a.reminderSentAt) return 'verstuurd'
  if (!isActive(a) || a.status !== 'bevestigd' || hasStarted(a, now)) return 'geen'
  const booked = zonedInstant(dateOf(a.receivedAt), timeOf(a.receivedAt) || '00:00')
  return hoursFromNow(startsAt(a), booked) < REMINDER_HOURS ? 'te-kort' : 'gepland'
}

/** Moment van de herinnering. */
export const reminderAt = (a: Pick<TimeSpan, 'date' | 'time'>) => new Date(startsAt(a).getTime() - REMINDER_HOURS * 3_600_000)

/**
 * Hoe vol een dag is: geboekte minuten tegenover open minuten (zonder blokkades), voor wie er die
 * dag werkt (of `opts.staffId`).
 */
export function dayLoad(
  hours: OpeningDay[],
  date: string,
  appointments: AgendaAppointment[],
  blocks: AgendaBlock[],
  opts: { staffId?: string; exceptions?: readonly SalonDayException[] } = {},
) {
  const w = salonDayFor(hours, opts.exceptions, date)
  const staffId = opts.staffId ?? w?.staffId ?? DEFAULT_STAFF_ID
  const open = w ? toMinutes(w.close) - toMinutes(w.open) : 0
  const blocked = blocks.filter((b) => b.date === date && b.staffId === staffId).reduce((s, b) => s + b.durationMinutes, 0)
  const booked = appointments
    .filter((a) => a.date === date && a.staffId === staffId && (isActive(a) || a.outcome === 'afgerond'))
    .reduce((s, a) => s + a.durationMinutes, 0)
  return { booked, available: Math.max(0, open - blocked), open: Boolean(w) }
}

/** Welke uren de tijdlijn toont: de openingstijden van die dagen, opgerekt tot alles erin past. */
export function visibleHours(
  hours: OpeningDay[],
  dates: string[],
  items: Pick<TimeSpan, 'time' | 'durationMinutes'>[],
  exceptions?: readonly SalonDayException[],
) {
  let start = Infinity
  let end = -Infinity
  dates.forEach((d) => {
    const w = salonDayFor(hours, exceptions, d)
    if (w) {
      start = Math.min(start, toMinutes(w.open))
      end = Math.max(end, toMinutes(w.close))
    }
  })
  items.forEach((i) => {
    start = Math.min(start, startMinutes(i))
    end = Math.max(end, endMinutes(i))
  })
  if (!Number.isFinite(start)) start = 9 * 60
  if (!Number.isFinite(end)) end = 18 * 60
  return { start: Math.floor(start / 60) * 60, end: Math.ceil(end / 60) * 60 }
}

/** Nu in minuten sinds middernacht, alleen als `date` vandaag is (voor de nu-lijn). */
export function nowMinutes(date: string, now = new Date()): number | null {
  if (date !== today()) return null
  return now.getHours() * 60 + now.getMinutes()
}
